import { areLinksEqual, isBidirectionalLink } from 'state/canvas/node-linking';
import { notEmpty } from 'utils/js';
import { set, sortBy } from 'lodash/fp';
import {
  SurfaceNodePointsTo,
  SurfaceNodeType,
  SurfaceNodeUnion,
} from 'graphql/types';

interface Response {
  nodes: SurfaceNodeUnion[];
  nodeEdges: SurfaceNodePointsTo[];
}

export default function usePrepareSurfaceNodes(
  nodesFromServer: SurfaceNodeUnion[],
  surfaceId: string,
): Response {
  nodesFromServer = nodesFromServer ?? ([] as SurfaceNodeUnion[]);

  const protocolNodes = nodesFromServer.filter(
    // @ts-ignore
    ({ type, contains }) => type === SurfaceNodeType.Protocol && contains,
  );

  const containingNodesPointingToProtocol = protocolNodes
    .filter((protocolNode) => {
      // @ts-ignore
      return protocolNode?.contains?.pointsTo?.find(
        (link) => link.to.id === protocolNode.id,
      );
    })
    // @ts-ignore
    .map((node) => node.contains);

  // We wanna have placeholder nodes first to have lower z index that's how konva assigns z index to elements
  const nodes = nodesFromServer
    // @ts-ignore
    .reduce((nodes, node) => {
      if (node.type === SurfaceNodeType.Protocol) {
        // Filter out links that point from protocol to it's containing node.
        // @ts-ignore
        const pointsTo = node?.pointsTo.filter(
          // @ts-ignore
          (link) => link.to.id !== node?.contains?.id,
        );

        const protocolNode = set('pointsTo', pointsTo, node);

        return [protocolNode, ...nodes];
      }
      return [...nodes, node];
    }, [])
    .map((node) => {
      const isPointingToProtocol = containingNodesPointingToProtocol.find(
        ({ id }) => id === node.id,
      );

      if (isPointingToProtocol) {
        // @ts-ignore
        const protocolNode = protocolNodes.find(
          // @ts-ignore
          ({ contains }) => contains?.id === node.id,
        ) as SurfaceNodeUnion;

        return {
          ...node,
          pointsTo: node.pointsTo.filter(
            (link) => link.to.id !== protocolNode?.id,
          ),
        };
      }
      return node;
    });

  const nodeEdges = usePrepareNodeEdges(nodes, surfaceId);

  return { nodes, nodeEdges };
}

export function usePrepareNodeEdges(
  nodes: SurfaceNodeUnion[],
  surfaceId: string,
): SurfaceNodePointsTo[] {
  const nodesWithEdges = nodes
    .filter(({ pointsTo = [] }) => notEmpty(pointsTo))
    .map(removeGlobalLinks(surfaceId));

  const allNodeEdges = nodesWithEdges
    .map(({ pointsTo }) => pointsTo)
    .filter(notEmpty)
    .flat();

  // Remove bi directional links HACK FOR NOW to represent link shapes as per current design implementation

  const nodeEdges = sortBy(
    (item) => item.settings.createdAt,
    allNodeEdges,
  ).reduce((nodeEdges, link) => {
    const exist = nodeEdges.some((otherLink) => areLinksEqual(otherLink, link));

    if (exist) {
      return nodeEdges.filter(
        (otherLink) => isBidirectionalLink(link, otherLink) === false,
      );
    }

    return nodeEdges;
  }, allNodeEdges);

  return nodeEdges;
}

function removeGlobalLinks(surfaceId) {
  return function (node) {
    return {
      ...node,
      pointsTo: node.pointsTo.filter(({ to }) => to?.surface?.id === surfaceId),
    };
  };
}
